import { persist, subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';
import type { StateCreator } from 'zustand/vanilla';

import { createDevtools } from '../middleware/create-dev-tools';
import { initialState, type SharingState } from './initial-state';
import { createPoliciesSlice, type SharingPoliciesAction } from './slices/policies/action';
import { createProfilesSlice, type SharingProfilesAction } from './slices/profiles/action';
import { createUserManagementSlice, type SharingUserManagementAction } from './slices/user-management/action';
import { createWizardUiSlice, type SharingWizardUiAction } from './slices/wizard-ui/action';

//  ===============  Aggregate createStoreFn ============ //

export type SharingStore = SharingState &
  SharingProfilesAction &
  SharingPoliciesAction &
  SharingUserManagementAction &
  SharingWizardUiAction;

const createStore: StateCreator<SharingStore, [['zustand/persist', unknown], ['zustand/devtools', never]]> = (...parameters) => ({
  ...initialState,
  ...createProfilesSlice(...parameters),
  ...createPoliciesSlice(...parameters),
  ...createUserManagementSlice(...parameters),
  ...createWizardUiSlice(...parameters),
});

//  ===============  Implement useStore ============ //

const devtools = createDevtools('sharing');

export const useSharingStore = createWithEqualityFn<SharingStore>()(
  subscribeWithSelector(
    persist(devtools(createStore), {
      name: 'rubidex-sharing-storage',
      partialize: (state) => ({
        // Persist only the in-progress "form in edition" state, not the full lists:
        // profile identity/description draft (profiles slice) + policies (policies slice)
        // + assigned users/groups (user-management slice) + wizard position (wizard-ui slice),
        // so reloading mid-edit resumes the whole form instead of losing policy/user edits.
        profileDraft: state.profileDraft,
        timeRules: state.timeRules,
        ipRules: state.ipRules,
        timezone: state.timezone,
        ipRestrictions: state.ipRestrictions,
        users: state.users,
        groups: state.groups,
        currentStep: state.currentStep,
        isEditMode: state.isEditMode,
      }),
    }),
  ),
  shallow,
);

export const getSharingStoreState = () => useSharingStore.getState();
